import React, { useCallback, useEffect } from "react";
import { useMutation, useQuery } from "react-query";
import {
    _api_get_employees_to_compensate,
    _api_get_hours_employee,
    _api_get_type_leave_depend_employee,
    _api_post_compensate,
} from "../api/supp_hours";
import { AVATAR_DEFAULT } from "../../../constants";
import { subtractDaysFromHours } from "../components/Dialog/functions";
import { DEFAULT_HOURS_VALUE, QUERY_KEYS } from "../constants";
import { useToastGlobal } from "../../../stores/useToastGlobal";
import { queryClient } from "../../..";

const useLogicCompensateSolde = ({
    openDefault = false,
    setOpen = () => console.log("Close Dialog"),
}) => {
    // * HOOKS
    // * HOOKS
    // * HOOKS
    const globalToast = useToastGlobal();

    // * STATES
    // * STATES
    // * STATES
    const [open, setOpenDialog] = React.useState(false); // pour le Dialog
    const [personConcerned, setPersonConcerned] = React.useState(null); // pour la personne concernée
    const [days, setdays] = React.useState(0); // pour le nombre de jours à ajuster
    const [daysError, setErrorDays] = React.useState(null); // pour l'erreur de jours
    const [soldeToAdjust, setSoldeToAdjust] = React.useState(null); // pour le solde à ajuster
    const [reason, setReason] = React.useState(""); // pour la raison de l'ajustement

    // * EFFECTS
    // * EFFECTS
    // * EFFECTS
    useEffect(() => {
        setOpenDialog(openDefault);
    }, [openDefault]);

    // * QUERIES & MUTATIONS
    // * QUERIES & MUTATIONS
    // * QUERIES & MUTATIONS
    const {
        data: employees,
        isLoading: isLoadingEmployees,
        isFetching: isFetchingEmployees,
    } = useQuery(
        [`${QUERY_KEYS}::employees_to_compensate`],
        () => {
            return _api_get_employees_to_compensate();
        },
        {
            enabled: open === true, // La requête ne s'exécute que si le dialog est ouvert
            refetchOnWindowFocus: false,
        }
    );

    const {
        data: balanceToAdjusted,
        isLoading: isLoadingBalanceToAdjusted,
        isFetching: isFetchingBalanceToAdjusted,
    } = useQuery(
        [`${QUERY_KEYS}::balance_to_adjusted`, personConcerned?.id_user],
        (data) => {
            return _api_get_type_leave_depend_employee({
                id_user: data.queryKey.at(1),
            });
        },
        {
            enabled: personConcerned !== null, // La requête ne s'exécute que si une personne est sélectionnée
            refetchOnWindowFocus: false,
            onSuccess: (res) => {
                if (res.success === false) {
                    globalToast.setShow({
                        visible: true,
                        content: res.message || "Une erreur s'est produite.",
                        level: "danger",
                    });
                    return;
                }
            },
        }
    );

    const {
        data: hoursSoldes,
        isLoading: isLoadingHoursSoldes,
        isFetching: isFetchingHoursSoldes,
    } = useQuery(
        [`${QUERY_KEYS}::hours_solde_employee`, personConcerned?.id_user],
        (data) => {
            return _api_get_hours_employee({
                id_user: data.queryKey.at(1),
            });
        },
        {
            enabled: personConcerned !== null, // La requête ne s'exécute que si une personne est sélectionnée
            refetchOnWindowFocus: false,
            onSuccess: (res) => {
                if (res.success === false) {
                    return alert(res.message);
                }
            },
        }
    );

    const { isLoading, mutate } = useMutation((data) => {
        return _api_post_compensate(data);
    });

    // * FUNCTIONS
    // * FUNCTIONS
    // * FUNCTIONS
    const closeDialog = () => {
        resetForm();
        setOpen(false);
        setOpenDialog(false);
        return;
    };

    const format_employees = useCallback(() => {
        return employees?.map((employee) => ({
            key: employee.id,
            header: employee.first_name + " " + employee.last_name,
            content: employee.job_title,
            image: employee.avatar || AVATAR_DEFAULT,
            email: employee.email,
            id_user: employee.id,
        }));
    }, [employees]);

    const format_balance_to_adjusted = () => {
        return balanceToAdjusted?.map((balance) => ({
            key: balance.id,
            header: balance.name,
        }));
    };

    const calculateHours = () => {
        const result = subtractDaysFromHours({
            beforeStr: hoursSoldes?.value,
            daysToSubtract: days,
            dayLengthStr: hoursSoldes?.user_hours_count_per_day,
        });
        return result;
    };

    const onSuccessMutate = (data) => {
        if (data?.success === false) {
            globalToast.setShow({
                visible: true,
                content:
                    data.message ||
                    "Une erreur s'est produite lors de la compensation.",
                level: "danger",
            });
            return;
        } else {
            globalToast.setShow({
                visible: true,
                content: "Compensation bien effectuée",
                level: "success",
            });
            // refetch de l'historique des soldes
            queryClient.invalidateQueries("leave::getListRows");
            closeDialog();
        }
        return;
    };

    const resetForm = () => {
        setPersonConcerned(null);
        setdays(0);
        setSoldeToAdjust(null);
        setReason("");
        return;
    };

    // * RETURN
    // * RETURN
    // * RETURN
    return {
        dialog: {
            open,
            setOpen: setOpenDialog,
            close: closeDialog,
        },
        view: {
            days,
            days_error: daysError,
            reason,
            personConcerned,
            soldeToAdjust,
            disabled_input_adjut_solde: personConcerned === null,
            disabled_button_confirm:
                soldeToAdjust === null || days <= 0 || personConcerned === null,
            disabled_button_reset:
                soldeToAdjust === null &&
                days <= 0 &&
                personConcerned === null &&
                reason === "",
            calculate_hours:
                personConcerned !== null
                    ? calculateHours()
                    : {
                          daysInHours: DEFAULT_HOURS_VALUE,
                          resultHours: DEFAULT_HOURS_VALUE,
                      },
            is_submiting_form: isLoading,
        },
        event_handlers: {
            onChangePersonConcerned: (person) => {
                return setPersonConcerned(() => person);
            },
            onChangeDays: (_, data) => {
                const value = data?.value || "";
                setdays(value);
                if (parseFloat(value) < 0) {
                    setErrorDays("la valeur ne peut être négative");
                } else {
                    setErrorDays(null);
                }
                return;
            },
            onChangeSoldeToAdjust: (solde) => {
                setSoldeToAdjust(solde);
                return;
            },
            onChangeReason: (reason) => {
                setReason(reason);
                return;
            },
            onResetForm: () => {
                return resetForm();
            },
            onSubmitForm: () => {
                mutate(
                    {
                        id_user: personConcerned.id_user,
                        solde_to_adjust: soldeToAdjust.key,
                        days,
                        reason,
                    },
                    {
                        onSuccess: onSuccessMutate,
                    }
                );
                return;
            },
        },
        queries: {
            employees: {
                data: Array.isArray(employees) ? format_employees() : [],
                isLoading: isLoadingEmployees,
                isFetching: isFetchingEmployees,
            },
            balance_to_adjusted: {
                data: Array.isArray(balanceToAdjusted)
                    ? format_balance_to_adjusted()
                    : [],
                isLoading: isLoadingBalanceToAdjusted,
                isFetching: isFetchingBalanceToAdjusted,
            },
            hours_soldes: {
                data: hoursSoldes,
                isLoading: isLoadingHoursSoldes,
                isFetching: isFetchingHoursSoldes,
            },
        },
    };
};

export default useLogicCompensateSolde;
